import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { calculateSmsCount, shouldShowApiToast } from '../../utils/helpers';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  getBroadcasts,
  getBroadcast,
  createBroadcast,
  updateBroadcast,
  deleteBroadcast,
  cancelBroadcast,
  rescheduleBroadcast,
  getBroadcastStats,
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  duplicateTemplate,
  clearApiMessage,
  clearError,
  clearBroadcastState,
  selectBroadcasts,
  selectBroadcastPagination,
  selectCurrentBroadcast,
  selectBroadcastSmsLogs,
  selectTemplates,
  selectBroadcastStats,
  selectBroadcastsLoading,
  selectBroadcastsApiMessage,
  selectBroadcastsError,
} from '../../store/slices/main/broadcastSlice';
import { 
  getMembers, 
  selectMembers,
  selectMemberPagination,
  selectAvailableGroups as selectMemberGroups
} from '../../store/slices/main/memberSlice';
import { 
  getVisitors, 
  selectVisitors,
  selectVisitorPagination 
} from '../../store/slices/growth/visitorSlice';
import { selectCurrentOrganization } from '../../store/slices/authSlice';
import { selectCurrentBranch } from '../../store/slices/system/branchSlice';
import { getGroups, selectGroups } from '../../store/slices/reports/groupSlice';
import { Lightbulb, Pencil, Trash2, Save, AlertTriangle, Clock, Search, Loader2, FileText, History, Send, Calendar, Bell, Download, Copy, Eye, BarChart3, ClipboardList, FileSpreadsheet, File, Mail, Smartphone, CheckCheck, User, Users, Users2, UserPlus } from 'lucide-react';

const Broadcast = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [activeTab, setActiveTab] = useState('compose');
  const [message, setMessage] = useState('');
  const [sendTo, setSendTo] = useState('all_members');
  const [senderId] = useState('CHURCH'); // ✅ Fixed default – backend handles sender IDs
  const [scheduleDate, setScheduleDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [broadcastFilter, setBroadcastFilter] = useState('');
  const [sampleMember, setSampleMember] = useState(null);
  
  // Dynamic data states
  const [dynamicOptions, setDynamicOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [memberGroups, setMemberGroups] = useState([]);
  const [visitorGroups, setVisitorGroups] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);

  // ==================== REFS ====================
  const editTemplateNameRef = useRef(null);
  const editTemplateTextRef = useRef(null);
  const editTemplateTypeRef = useRef(null);
  const createTemplateNameRef = useRef(null);
  const createTemplateTextRef = useRef(null);
  const createTemplateTypeRef = useRef(null);
  const scheduleDateRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const broadcasts = useSelector(selectBroadcasts) || [];
  const pagination = useSelector(selectBroadcastPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const currentBroadcast = useSelector(selectCurrentBroadcast);
  const broadcastSmsLogs = useSelector(selectBroadcastSmsLogs) || [];
  const templates = useSelector(selectTemplates) || [];
  const stats = useSelector(selectBroadcastStats);
  const loading = useSelector(selectBroadcastsLoading);
  const apiMessage = useSelector(selectBroadcastsApiMessage);
  const error = useSelector(selectBroadcastsError);
  
  // Members and Visitors data
  const members = useSelector(selectMembers) || [];
  const memberPagination = useSelector(selectMemberPagination) || {};
  const visitors = useSelector(selectVisitors) || [];
  const visitorPagination = useSelector(selectVisitorPagination) || {};
  
  const organization = useSelector(selectCurrentOrganization);
  const currentBranch = useSelector(selectCurrentBranch);
  const groupList = useSelector(selectGroups) || [];

  // ==================== EFFECTS ====================

  // Load initial data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoadingOptions(true);
      
      try {
        await Promise.all([
          dispatch(getBroadcasts({ per_page: itemsPerPage, page: currentPage })),
          dispatch(getTemplates()),
          dispatch(getBroadcastStats()),
        ]);
        
        await Promise.all([
          dispatch(getMembers({ per_page: 50 })),
          dispatch(getVisitors({ per_page: 50 })),
          dispatch(getGroups({ per_page: 100 })),
        ]);
      } catch (err) {
        console.error('Error loading initial data:', err);
      } finally {
        setIsLoadingOptions(false);
      }
    };
    
    loadInitialData();
    
    return () => {
      dispatch(clearBroadcastState());
    };
  }, [dispatch]);

  // Get sample member for merge field preview
  useEffect(() => {
    if (members.length > 0) {
      setSampleMember(members[0]);
    } else {
      setSampleMember(null);
    }
  }, [members]);

  // Build dynamic options whenever data changes
  useEffect(() => {
    buildDynamicOptions();
  }, [members, visitors, memberPagination, visitorPagination, groupList]);

  // Handle filters
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (broadcastFilter) params.status = broadcastFilter;
      
      dispatch(getBroadcasts(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, broadcastFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;
    
    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== BUILD DYNAMIC OPTIONS ====================
  
  const buildDynamicOptions = () => {
    const options = [];
    
    const memberCount = memberPagination?.total || members.length || 0;
    const activeMemberCount = members.filter(m => m.status === 'Active').length;
    const inactiveMemberCount = members.filter(m => m.status === 'Inactive').length;
    
    options.push(
      { 
        id: 'all_members', 
        value: 'all_members', 
        label: `🌐 All Members (${memberCount})`,
        type: 'members',
        group: null
      },
      { 
        id: 'active_members', 
        value: 'active_members', 
        label: `✅ Active Members (${activeMemberCount})`,
        type: 'members',
        group: null
      },
      { 
        id: 'inactive_members', 
        value: 'inactive_members', 
        label: `⭕ Inactive Members (${inactiveMemberCount})`,
        type: 'members',
        group: null
      }
    );
    
    const visitorCount = visitorPagination?.total || visitors.length || 0;
    
    options.push(
      { 
        id: 'all_visitors', 
        value: 'all_visitors', 
        label: `👋 All Visitors (${visitorCount})`,
        type: 'visitors',
        group: null
      }
    );
    
    // Extract unique groups
    let groups = [];
    
    if (memberPagination?.groups && Array.isArray(memberPagination.groups)) {
      groups = memberPagination.groups;
    } 
    else if (groupList && groupList.length > 0) {
      groups = groupList.map(g => g.name || g.title || g.group_name || g.label).filter(Boolean);
    }
    else if (members && members.length > 0) {
      const uniqueGroups = new Set();
      members.forEach(m => {
        if (m.group) uniqueGroups.add(m.group);
      });
      groups = Array.from(uniqueGroups);
    }
    
    groups.forEach(group => {
      const groupMemberCount = members.filter(m => m.group === group).length;
      options.push({
        id: `group_${group}`,
        value: `group:${group}`,
        label: `👥 ${group} (${groupMemberCount} members)`,
        type: 'group',
        group: group
      });
    });
    
    // Visitor sources
    const visitorSources = new Set();
    visitors.forEach(v => {
      if (v.source) visitorSources.add(v.source);
      if (v.referred_by) visitorSources.add(v.referred_by);
      if (v.visitor_type) visitorSources.add(v.visitor_type);
    });
    
    Array.from(visitorSources).filter(Boolean).forEach(source => {
      const sourceCount = visitors.filter(v => 
        v.source === source || v.referred_by === source || v.visitor_type === source
      ).length;
      options.push({
        id: `visitor_source_${source}`,
        value: `visitor_source:${source}`,
        label: `📌 ${source} (${sourceCount} visitors)`,
        type: 'visitor_source',
        group: source
      });
    });
    
    // Branches
    const branches = new Set();
    members.forEach(m => {
      if (m.branch) branches.add(m.branch);
    });
    visitors.forEach(v => {
      if (v.branch) branches.add(v.branch);
    });
    if (currentBranch?.name) branches.add(currentBranch.name);
    
    Array.from(branches).filter(Boolean).forEach(branch => {
      const memberCount = members.filter(m => m.branch === branch).length;
      const visitorCount = visitors.filter(v => v.branch === branch).length;
      const total = memberCount + visitorCount;
      options.push({
        id: `branch_${branch}`,
        value: `branch:${branch}`,
        label: `🏛️ ${branch} (${total} people)`,
        type: 'branch',
        group: branch
      });
    });
    
    // New members (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const newMembers = members.filter(m => {
      const joinedDate = new Date(m.created_at || m.joined_date);
      return joinedDate >= thirtyDaysAgo;
    });
    if (newMembers.length > 0) {
      options.push({
        id: 'new_members',
        value: 'new_members',
        label: `🌟 New Members (${newMembers.length})`,
        type: 'members',
        group: null
      });
    }
    
    // Recent visitors (last 30 days)
    const recentVisitors = visitors.filter(v => {
      const visitedDate = new Date(v.visited_at || v.created_at);
      return visitedDate >= thirtyDaysAgo;
    });
    if (recentVisitors.length > 0) {
      options.push({
        id: 'recent_visitors',
        value: 'recent_visitors',
        label: `🔔 Recent Visitors (${recentVisitors.length})`,
        type: 'visitors',
        group: null
      });
    }
    
    setDynamicOptions(options);
  };

  // ==================== DYNAMIC MERGE FIELDS ====================

  const getFieldValue = (key) => {
    const member = sampleMember || {};
    const organizationName = typeof organization === 'string'
      ? organization
      : organization?.name || organization?.title || organization?.organization_name || '';
    const displayValue = (value) => value?.name || value || '';
    const firstName = member.first_name || '';
    const lastName = member.last_name || '';

    const values = {
      '{FirstName}': firstName,
      '{LastName}': lastName,
      '{FullName}': member.name || member.full_name || `${firstName} ${lastName}`.trim(),
      '{ChurchName}': organizationName,
      '{PastorName}': typeof organization === 'object' ? organization?.pastor_name || organization?.pastorName || '' : '',
      '{Group}': displayValue(member.group),
      '{Branch}': displayValue(member.branch) || currentBranch?.name || '',
      '{Email}': member.email || '',
      '{Phone}': member.phone || '',
      '{Status}': displayValue(member.status)
    };

    return values[key] || '';
  };

  const mergeFields = [
    { key: '{FirstName}', label: 'First Name', description: "Member's first name", example: getFieldValue('{FirstName}') },
    { key: '{LastName}', label: 'Last Name', description: "Member's last name", example: getFieldValue('{LastName}') },
    { key: '{FullName}', label: 'Full Name', description: "Member's full name", example: getFieldValue('{FullName}') },
    { key: '{ChurchName}', label: 'Church Name', description: 'Your church name', example: getFieldValue('{ChurchName}') },
    { key: '{PastorName}', label: 'Pastor Name', description: "Pastor's name", example: getFieldValue('{PastorName}') },
    { key: '{Group}', label: 'Group', description: "Member's group", example: getFieldValue('{Group}') },
    { key: '{Branch}', label: 'Branch', description: "Member's branch", example: getFieldValue('{Branch}') },
    { key: '{Email}', label: 'Email', description: "Member's email", example: getFieldValue('{Email}') },
    { key: '{Phone}', label: 'Phone', description: "Member's phone number", example: getFieldValue('{Phone}') },
    { key: '{Status}', label: 'Status', description: "Member's status", example: getFieldValue('{Status}') },
  ];

  // ==================== HELPERS ====================

  const smsCount = calculateSmsCount(message);
  const charCount = message.length;

  const getPreviewMessage = (text) => {
    if (!text) return 'Your message will appear here…';
    
    let preview = text;
    const replacements = Object.fromEntries(mergeFields.map(({ key }) => [key, getFieldValue(key)]));
    
    for (const [key, value] of Object.entries(replacements)) {
      preview = preview.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
    }
    
    return preview;
  };

  // Filter templates
  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.text?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = !templateFilter || t.type === templateFilter;
    return matchesSearch && matchesFilter;
  });

  // Pagination for templates
  const totalItems = filteredTemplates.length;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTemplates = filteredTemplates.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const getBroadcastPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    const totalPages = pagination.last_page || 1;
    const currentPageNum = pagination.current_page || 1;
    
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const insertField = (field) => {
    setMessage(prev => prev + field);
  };

  const applyTemplate = (template) => {
    setMessage(template.text);
    setActiveTab('compose');
    showToastMessage('📋 Template loaded!', 'success');
  };

  const switchTab = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
  };

  // ==================== TEMPLATE CRUD ====================

  const handleCreateTemplate = () => {
    setTimeout(() => {
      if (createTemplateNameRef.current) createTemplateNameRef.current.value = '';
      if (createTemplateTextRef.current) createTemplateTextRef.current.value = '';
      if (createTemplateTypeRef.current) createTemplateTypeRef.current.value = '';
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><FileText size={16} style={{ verticalAlign: '-3px' }} /> Create New Template</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Template Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              ref={createTemplateNameRef}
              placeholder="e.g. Sunday Service Reminder"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Template Type</label>
            <select 
              className="form-select" 
              ref={createTemplateTypeRef}
              defaultValue=""
            >
              <option value="">Select Type</option>
              <option value="service">Service</option>
              <option value="birthday">Birthday</option>
              <option value="welcome">Welcome</option>
              <option value="event">Event</option>
              <option value="prayer">Prayer</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Template Text <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="4"
              ref={createTemplateTextRef}
              placeholder="Enter your template text..."
            />
            <div className="form-hint">
              Use {'{FirstName}'}, {'{LastName}'}, {'{FullName}'}, {'{ChurchName}'}, {'{PastorName}'} for personalisation
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Available Merge Fields</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {mergeFields.map(field => (
                <button 
                  key={field.key}
                  className="btn btn-ghost btn-sm" 
                  onClick={() => {
                    const textarea = createTemplateTextRef.current;
                    if (textarea) {
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const value = textarea.value;
                      textarea.value = value.substring(0, start) + field.key + value.substring(end);
                      textarea.focus();
                      textarea.selectionStart = textarea.selectionEnd = start + field.key.length;
                    }
                  }}
                  title={`${field.label}: ${field.description} (e.g. ${field.example})`}
                >
                  {field.key}
                </button>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
              <Lightbulb size={14} style={{ verticalAlign: '-3px' }} /> Click a merge field to insert it at the cursor position
            </div>
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-mid)'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}><Smartphone size={14} style={{ verticalAlign: '-3px' }} /> Preview</div>
            <div style={{ 
              background: 'var(--navy)', 
              borderRadius: '8px', 
              padding: '10px',
              color: 'rgba(255,255,255,.85)',
              fontSize: '12px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              minHeight: '40px'
            }}>
              {createTemplateTextRef.current?.value 
                ? getPreviewMessage(createTemplateTextRef.current.value)
                : 'Your message preview will appear here...'}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const name = createTemplateNameRef.current?.value?.trim() || '';
              const text = createTemplateTextRef.current?.value?.trim() || '';
              const type = createTemplateTypeRef.current?.value || '';
              
              if (!name) {
                showToastMessage('Please enter a template name', 'error');
                return;
              }
              if (!text) {
                showToastMessage('Please enter template content', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(createTemplate({ name, text, type }));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getTemplates());
                  showToastMessage(`✅ "${name}" created successfully!`, 'success');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to create template', 'error');
                }
              } catch (err) {
                showToastMessage('Failed to create template', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Template'}
          </button>
        </div>
      </div>
    );
  };

  const handleEditTemplate = (template) => {
    setEditingTemplateId(template.id);
    
    setTimeout(() => {
      if (editTemplateNameRef.current) editTemplateNameRef.current.value = template.name || '';
      if (editTemplateTextRef.current) editTemplateTextRef.current.value = template.text || '';
      if (editTemplateTypeRef.current) editTemplateTypeRef.current.value = template.type || '';
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Template</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Template Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              ref={editTemplateNameRef}
              defaultValue={template.name || ''}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Template Type</label>
            <select 
              className="form-select" 
              ref={editTemplateTypeRef}
              defaultValue={template.type || ''}
            >
              <option value="">Select Type</option>
              <option value="service">Service</option>
              <option value="birthday">Birthday</option>
              <option value="welcome">Welcome</option>
              <option value="event">Event</option>
              <option value="prayer">Prayer</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Template Text <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="4"
              ref={editTemplateTextRef}
              defaultValue={template.text || ''}
            />
            <div className="form-hint">
              Use {'{FirstName}'}, {'{LastName}'}, {'{FullName}'}, {'{ChurchName}'}, {'{PastorName}'} for personalisation
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Available Merge Fields</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {mergeFields.map(field => (
                <button 
                  key={field.key}
                  className="btn btn-ghost btn-sm" 
                  onClick={() => {
                    const textarea = editTemplateTextRef.current;
                    if (textarea) {
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const value = textarea.value;
                      textarea.value = value.substring(0, start) + field.key + value.substring(end);
                      textarea.focus();
                      textarea.selectionStart = textarea.selectionEnd = start + field.key.length;
                    }
                  }}
                  title={`${field.label}: ${field.description} (e.g. ${field.example})`}
                >
                  {field.key}
                </button>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
              <Lightbulb size={14} style={{ verticalAlign: '-3px' }} /> Click a merge field to insert it at the cursor position
            </div>
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-mid)'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}><Smartphone size={14} style={{ verticalAlign: '-3px' }} /> Preview</div>
            <div style={{ 
              background: 'var(--navy)', 
              borderRadius: '8px', 
              padding: '10px',
              color: 'rgba(255,255,255,.85)',
              fontSize: '12px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              minHeight: '40px'
            }}>
              {editTemplateTextRef.current?.value 
                ? getPreviewMessage(editTemplateTextRef.current.value)
                : 'Your message preview will appear here...'}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const name = editTemplateNameRef.current?.value?.trim() || '';
              const text = editTemplateTextRef.current?.value?.trim() || '';
              const type = editTemplateTypeRef.current?.value || '';
              
              if (!name) {
                showToastMessage('Please enter a template name', 'error');
                return;
              }
              if (!text) {
                showToastMessage('Please enter template content', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(updateTemplate({ id: template.id, data: { name, text, type } }));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getTemplates());
                  showToastMessage(`✅ "${name}" updated successfully!`, 'success');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to update template', 'error');
                }
              } catch (err) {
                showToastMessage('Failed to update template', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Saving...' : <><Save size={16} style={{ verticalAlign: '-3px' }} /> Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteTemplate = (template) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Template</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{template.name}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will permanently remove the template.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteTemplate(template.id));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getTemplates());
                  showToastMessage(`🗑️ "${template.name}" deleted`, 'error');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to delete template', 'error');
                }
              } catch (err) {
                showToastMessage('Failed to delete template', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Delete Template
          </button>
        </div>
      </div>
    );
  };

  const handleDuplicateTemplate = (template) => {
    setIsSubmitting(true);
    dispatch(duplicateTemplate(template.id))
      .then(async (result) => {
        if (result.payload?.success) {
          await dispatch(getTemplates());
          showToastMessage(`📋 "${template.name} (Copy)" created`, 'success');
        } else if (result.error) {
          showToastMessage(result.payload?.message || 'Failed to duplicate template', 'error');
        }
      })
      .catch(() => {
        showToastMessage('Failed to duplicate template', 'error');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // ==================== BROADCAST CRUD ====================

  const handleSendBroadcast = () => {
    if (!message.trim()) {
      showToastMessage('Please write a message first', 'error');
      return;
    }

    const selectedOption = dynamicOptions.find(opt => opt.value === sendTo);
    const displayLabel = selectedOption?.label || sendTo;

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px' }} /> Confirm Broadcast</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--cream)', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Message Preview</div>
            <div style={{ fontSize: '13px', color: 'var(--text)', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
              {getPreviewMessage(message)}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: '13px' }}>
            <span style={{ color: 'var(--text-light)' }}>To</span>
            <span style={{ fontWeight: '600' }}>{displayLabel}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: '13px' }}>
            <span style={{ color: 'var(--text-light)' }}>SMS Count</span>
            <span style={{ fontWeight: '600' }}>{smsCount}</span>
          </div>
          {scheduleDate && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)' }}>Scheduled For</span>
              <span style={{ fontWeight: '600' }}>{new Date(scheduleDate).toLocaleString()}</span>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(createBroadcast({
                  message: message.trim(),
                  send_to: sendTo,
                  // sender_id: senderId,
                  schedule_date: scheduleDate || null
                }));
                if (result.payload?.success) {
                  closeModal();
                  setMessage('');
                  setScheduleDate('');
                  dispatch(getBroadcasts({ per_page: itemsPerPage, page: currentPage }));
                  dispatch(getBroadcastStats());
                  showToastMessage(scheduleDate ? '✅ Broadcast scheduled successfully!' : '✅ Broadcast sent successfully!', 'success');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to send broadcast', 'error');
                }
              } catch (err) {
                showToastMessage('Failed to send broadcast', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Sending...' : scheduleDate ? <><Calendar size={16} style={{ verticalAlign: '-3px' }} /> Schedule</> : <><Send size={16} style={{ verticalAlign: '-3px' }} /> Send Now</>}
          </button>
        </div>
      </div>
    );
  };

  const handleSaveDraft = () => {
    if (!message.trim()) {
      showToastMessage('Please write a message first', 'error');
      return;
    }

    setIsSubmitting(true);
    dispatch(createBroadcast({
      message: message.trim(),
      send_to: sendTo,
      // sender_id: senderId,
      status: 'draft'
    }))
      .then((result) => {
        if (result.payload?.success) {
          showToastMessage('💾 Draft saved!', 'success');
          dispatch(getBroadcasts({ per_page: itemsPerPage, page: currentPage }));
          setMessage('');
        } else if (result.error) {
          showToastMessage(result.payload?.message || 'Failed to save draft', 'error');
        }
      })
      .catch(() => {
        showToastMessage('Failed to save draft', 'error');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleTestSend = () => {
    if (!message.trim()) {
      showToastMessage('Please write a message first', 'error');
      return;
    }
    showToastMessage('🔔 Test SMS sent to your number!', 'success');
  };

  const handleDeleteBroadcast = (broadcast) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Broadcast</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete this broadcast?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will permanently delete the broadcast.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteBroadcast(broadcast.id));
                if (result.payload?.success) {
                  closeModal();
                  dispatch(getBroadcasts({ per_page: itemsPerPage, page: currentPage }));
                  dispatch(getBroadcastStats());
                  showToastMessage('Broadcast deleted successfully', 'error');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to delete broadcast', 'error');
                }
              } catch (err) {
                showToastMessage('Failed to delete broadcast', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Delete Broadcast
          </button>
        </div>
      </div>
    );
  };

  const handleCancelBroadcast = (broadcast) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Clock size={16} style={{ verticalAlign: '-3px' }} /> Cancel Scheduled Broadcast</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to cancel this scheduled broadcast?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will cancel the scheduled broadcast.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(cancelBroadcast(broadcast.id));
                if (result.payload?.success) {
                  closeModal();
                  dispatch(getBroadcasts({ per_page: itemsPerPage, page: currentPage }));
                  dispatch(getBroadcastStats());
                  showToastMessage('Broadcast cancelled successfully', 'info');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to cancel broadcast', 'error');
                }
              } catch (err) {
                showToastMessage('Failed to cancel broadcast', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Cancel Broadcast
          </button>
        </div>
      </div>
    );
  };

  const handleRescheduleBroadcast = (broadcast) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Clock size={16} style={{ verticalAlign: '-3px' }} /> Reschedule Broadcast</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">New Date & Time <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              type="datetime-local" 
              ref={scheduleDateRef}
              defaultValue={broadcast.schedule_date ? new Date(broadcast.schedule_date).toISOString().slice(0, 16) : ''}
            />
          </div>
          <div style={{ 
            padding: '10px 12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)'
          }}>
            Current: {broadcast.schedule_date ? new Date(broadcast.schedule_date).toLocaleString() : 'Not scheduled'}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const newDate = scheduleDateRef.current?.value;
              if (!newDate) {
                showToastMessage('Please select a new date and time', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(rescheduleBroadcast({ 
                  id: broadcast.id, 
                  schedule_date: newDate 
                }));
                if (result.payload?.success) {
                  closeModal();
                  dispatch(getBroadcasts({ per_page: itemsPerPage, page: currentPage }));
                  dispatch(getBroadcastStats());
                  showToastMessage('Broadcast rescheduled successfully', 'success');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to reschedule broadcast', 'error');
                }
              } catch (err) {
                showToastMessage('Failed to reschedule broadcast', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Reschedule
          </button>
        </div>
      </div>
    );
  };

  const handleViewBroadcast = (broadcast) => {
    dispatch(getBroadcast(broadcast.id));
    
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Mail size={16} style={{ verticalAlign: '-3px' }} /> Broadcast Details</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            {[
              ['Status', `<span class="badge ${broadcast.status === 'sent' ? 'badge-green' : broadcast.status === 'scheduled' ? 'badge-amber' : broadcast.status === 'cancelled' ? 'badge-red' : 'badge-navy'}">${broadcast.status || 'Draft'}</span>`],
              ['To', broadcast.send_to || 'N/A'],
              ['Sender ID', broadcast.sender_id || 'N/A'],
              ['Recipients', broadcast.recipient_count || 0],
              ['Created', broadcast.created_at ? new Date(broadcast.created_at).toLocaleString() : 'N/A'],
              ['Sent', broadcast.sent_at ? new Date(broadcast.sent_at).toLocaleString() : 'Not sent'],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: '10px 12px', background: 'var(--cream)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '4px' }}>{label}</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }} dangerouslySetInnerHTML={{ __html: value }}></div>
              </div>
            ))}
          </div>
          
          <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Message</div>
            <div style={{ fontSize: '13px', color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{broadcast.message}</div>
          </div>
          
          {broadcastSmsLogs.length > 0 && (
            <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '8px' }}>Recent SMS Logs</div>
              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {broadcastSmsLogs.slice(0, 5).map((log, index) => (
                  <div key={index} style={{ fontSize: '12px', padding: '4px 0', borderBottom: '1px solid var(--border-light)' }}>
                    {log.phone} · {log.status} · {log.sent_at ? new Date(log.sent_at).toLocaleString() : 'N/A'}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          {broadcast.status === 'scheduled' && (
            <>
              <button className="btn btn-gold btn-sm" onClick={() => {
                closeModal();
                setTimeout(() => handleRescheduleBroadcast(broadcast), 100);
              }}>
                <Clock size={14} style={{ verticalAlign: '-2px' }} /> Reschedule
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => {
                closeModal();
                setTimeout(() => handleCancelBroadcast(broadcast), 100);
              }}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  // ==================== EXPORT ====================

  const openExportModal = () => {
    const data = activeTab === 'templates' ? filteredTemplates : broadcasts;
    if (data.length === 0) {
      showToastMessage('No data to export', 'error');
      return;
    }

    const fields = activeTab === 'templates' 
      ? [
          { key: 'name', label: 'Template Name' },
          { key: 'text', label: 'Template Text' },
          { key: 'type', label: 'Type' }
        ]
      : [
          { key: 'id', label: 'ID' },
          { key: 'message', label: 'Message' },
          { key: 'send_to', label: 'Sent To' },
          { key: 'recipient_count', label: 'Recipients' },
          { key: 'sender_id', label: 'Sender ID' },
          { key: 'status', label: 'Status' },
          { key: 'sent_at', label: 'Sent At' },
          { key: 'created_at', label: 'Created At' }
        ];

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> Export {activeTab === 'templates' ? 'Templates' : 'Broadcasts'}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} record{data.length !== 1 ? 's' : ''} in your preferred format.
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const formattedData = formatForExport(data, fields);
                    const filename = getExportFilename(activeTab === 'templates' ? 'templates' : 'broadcasts');
                    
                    switch (format) {
                      case 'CSV':
                        exportToCSV(formattedData, filename);
                        break;
                      case 'JSON':
                        exportToJSON(formattedData, filename);
                        break;
                      case 'Excel':
                        exportToExcel(formattedData, filename);
                        break;
                      case 'PDF':
                        exportToPDF(formattedData, `${activeTab === 'templates' ? 'Templates' : 'Broadcasts'} Report`, filename);
                        break;
                      default:
                        showToastMessage('Export format not supported', 'error');
                        return;
                    }
                    showToastMessage(`📥 ${data.length} records exported as ${format}!`, 'success');
                    closeModal();
                  }}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '20px', display: 'flex' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <ClipboardList size={20} />}
                    {format === 'Excel' && <FileSpreadsheet size={20} />}
                    {format === 'PDF' && <File size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> {data.length} records will be exported</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  const previewMessage = getPreviewMessage(message);
  const selectedOption = dynamicOptions.find(opt => opt.value === sendTo);
  const selectedLabel = selectedOption?.label || sendTo;

  return (
    <div className="grid-65-35">
      <div>
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-title" style={{ marginBottom: '16px' }}><Send size={16} style={{ verticalAlign: '-3px' }} /> Broadcast</div>
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'compose' ? 'active' : ''}`}
              onClick={() => switchTab('compose')}
            >
              <Pencil size={14} style={{ verticalAlign: '-2px' }} /> Compose
            </button>
            <button
              className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
              onClick={() => switchTab('templates')}
            >
              <ClipboardList size={14} style={{ verticalAlign: '-2px' }} /> Templates
            </button>
            <button
              className={`tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => switchTab('history')}
            >
              <History size={14} style={{ verticalAlign: '-2px' }} /> History
            </button>
          </div>

          {/* Compose Tab */}
          {activeTab === 'compose' && (
            <div id="compose-pane">
              <div className="form-group">
                <label className="form-label">Send To</label>
                <select 
                  className="form-select" 
                  value={sendTo} 
                  onChange={(e) => setSendTo(e.target.value)}
                  disabled={isLoadingOptions}
                >
                  {isLoadingOptions ? (
                    <option value="">Loading options...</option>
                  ) : dynamicOptions.length > 0 ? (
                    dynamicOptions.map(option => (
                      <option key={option.id} value={option.value}>
                        {option.label}
                      </option>
                    ))
                  ) : (
                    <option value="">No options available</option>
                  )}
                </select>
                <div className="form-hint" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <span>👥 {members.length} members loaded</span>
                  <span>👋 {visitors.length} visitors loaded</span>
                  <span>📊 {dynamicOptions.length} recipient options</span>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea 
                  className="form-textarea" 
                  rows="6" 
                  value={message} 
                  onChange={(e) => setMessage(e.target.value)} 
                  placeholder="Type your message here…&#10;&#10;Use {FirstName}, {LastName}, {FullName}, {ChurchName}, {PastorName} for personalisation" 
                />
                <div className="char-count" style={{ fontSize: '11px', color: 'var(--text-light)', textAlign: 'right', marginTop: '4px' }}>
                  {charCount} characters · {smsCount} SMS
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Merge Fields</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {mergeFields.map(f => (
                    <button 
                      key={f.key} 
                      className="btn btn-ghost btn-sm" 
                      onClick={() => insertField(f.key)} 
                      title={`${f.label}: ${f.description} (e.g. ${f.example})`}
                    >
                      {f.key}
                    </button>
                  ))}
                </div>
                <div style={{ 
                  fontSize: '11px', 
                  color: 'var(--text-light)', 
                  marginTop: '4px',
                  padding: '8px 12px',
                  background: 'var(--cream)',
                  borderRadius: '6px'
                }}>
                  <Lightbulb size={14} style={{ verticalAlign: '-3px' }} /> Click a merge field to insert it at the cursor position
                  {sampleMember && (
                    <span style={{ marginLeft: '8px' }}>
                      · Preview: {sampleMember.first_name} {sampleMember.last_name}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--navy)' }}>Schedule for later?</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Leave blank to send immediately</div>
                </div>
                <input 
                  className="form-input" 
                  type="datetime-local" 
                  style={{ width: 'auto' }} 
                  value={scheduleDate} 
                  onChange={(e) => setScheduleDate(e.target.value)} 
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleSaveDraft}
                  disabled={isSubmitting}
                  title="Save draft"
                >
                  <Save size={14} style={{ verticalAlign: '-2px' }} /> Save Draft
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleTestSend}
                  title="Send test SMS"
                >
                  <Bell size={14} style={{ verticalAlign: '-2px' }} /> Test Send
                </button>
                <button
                  className="btn btn-gold btn-sm"
                  style={{ flex: 1 }}
                  onClick={handleSendBroadcast}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <><Loader2 size={14} style={{ verticalAlign: '-2px' }} /> Processing...</> : scheduleDate ? <><Calendar size={14} style={{ verticalAlign: '-2px' }} /> Schedule</> : <><Send size={14} style={{ verticalAlign: '-2px' }} /> Send Now</>}
                </button>
              </div>
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div id="templates-pane">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
                <div className="search-box" style={{ maxWidth: '200px', flex: '1' }}>
                  <span className="search-icon"><Search size={14} /></span>
                  <input 
                    type="text" 
                    placeholder="Search templates..." 
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <select 
                  className="form-select" 
                  style={{ width: '140px' }}
                  value={templateFilter}
                  onChange={(e) => {
                    setTemplateFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Types</option>
                  <option value="service">Service</option>
                  <option value="birthday">Birthday</option>
                  <option value="welcome">Welcome</option>
                  <option value="event">Event</option>
                  <option value="prayer">Prayer</option>
                  <option value="custom">Custom</option>
                </select>
                <button 
                  className="btn btn-gold btn-sm" 
                  onClick={handleCreateTemplate}
                  disabled={isSubmitting}
                >
                  + New Template
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={openExportModal}
                  title="Export templates"
                >
                  <Download size={14} style={{ verticalAlign: '-2px' }} /> Export
                </button>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ marginBottom: '8px' }}><Loader2 size={20} /></div>
                  <div style={{ color: 'var(--text-light)' }}>Loading templates...</div>
                </div>
              ) : currentTemplates.length > 0 ? (
                currentTemplates.map(t => (
                  <div 
                    key={t.id} 
                    style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '14px', marginBottom: '10px', cursor: 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--gold)'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '6px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
                        {t.name}
                        {t.type && <span className="badge badge-navy" style={{ marginLeft: '8px', fontSize: '9px' }}>{t.type}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => applyTemplate(t)}>Use Template</button>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleEditTemplate(t)} title="Edit"><Pencil size={16} /></button>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleDuplicateTemplate(t)} title="Duplicate" disabled={isSubmitting}><Copy size={16} /></button>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleDeleteTemplate(t)} title="Delete" style={{ color: 'var(--red)' }}><Trash2 size={16} /></button>
                      </div>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-mid)', lineHeight: '1.6' }}>
                      {t.text && t.text.length > 100 ? `${t.text.substring(0, 100)}…` : t.text}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <div className="empty-icon"><ClipboardList size={40} strokeWidth={1.5} /></div>
                  <div className="empty-title">No templates found</div>
                  <div className="empty-text">
                    {searchTerm || templateFilter ? 'Try adjusting your search or filters' : 'Create a template to get started'}
                  </div>
                  {!searchTerm && !templateFilter && (
                    <button className="btn btn-gold btn-sm" onClick={handleCreateTemplate}>
                      + Create Your First Template
                    </button>
                  )}
                </div>
              )}

              {/* Template Pagination */}
              {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid var(--border-light)',
                  marginTop: '4px'
                }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems}
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button 
                      className="btn btn-ghost btn-sm"
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      style={{ opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                    >
                      ←
                    </button>
                    {getPageNumbers().map(page => (
                      <button 
                        key={page}
                        className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button 
                      className="btn btn-ghost btn-sm"
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      style={{ opacity: currentPage === totalPages ? 0.5 : 1, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                    >
                      →
                    </button>
                  </div>
                  <select 
                    className="form-select" 
                    style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={3}>3</option>
                    <option value={6}>6</option>
                    <option value={9}>9</option>
                    <option value={12}>12</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div id="history-pane">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
                <div className="search-box" style={{ maxWidth: '200px', flex: '1' }}>
                  <span className="search-icon"><Search size={14} /></span>
                  <input 
                    type="text" 
                    placeholder="Search broadcasts..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <select 
                  className="form-select" 
                  style={{ width: '140px' }}
                  value={broadcastFilter}
                  onChange={(e) => setBroadcastFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="sent">Sent</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="draft">Draft</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="failed">Failed</option>
                </select>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowStats(!showStats)}
                >
                  <BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> Stats
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={openExportModal}
                >
                  <Download size={14} style={{ verticalAlign: '-2px' }} /> Export
                </button>
              </div>

              {/* Stats Summary */}
              {showStats && stats && (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(5, 1fr)', 
                  gap: '10px',
                  marginBottom: '16px',
                  padding: '12px',
                  background: 'var(--cream)',
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--navy)' }}>{stats.total || 0}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Total</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--green)' }}>{stats.sent || 0}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Sent</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--amber)' }}>{stats.scheduled || 0}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Scheduled</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-light)' }}>{stats.drafts || 0}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Drafts</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--red)' }}>{stats.failed || 0}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Failed</div>
                  </div>
                </div>
              )}

              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ marginBottom: '8px' }}><Loader2 size={20} /></div>
                  <div style={{ color: 'var(--text-light)' }}>Loading broadcasts...</div>
                </div>
              ) : broadcasts.length > 0 ? (
                broadcasts.map(b => {
                  const statusBadge = b.status === 'sent' ? 'badge-green' : 
                                     b.status === 'scheduled' ? 'badge-amber' : 
                                     b.status === 'cancelled' ? 'badge-red' : 'badge-navy';
                  
                  return (
                    <div key={b.id} style={{ 
                      border: '1px solid var(--border)', 
                      borderRadius: '10px', 
                      padding: '14px', 
                      marginBottom: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--gold)'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                    onClick={() => handleViewBroadcast(b)}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
                            {b.message && b.message.length > 50 ? `${b.message.substring(0, 50)}...` : b.message}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
                            To: {b.send_to || 'N/A'} · {b.recipient_count || 0} recipients
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                            {b.created_at ? new Date(b.created_at).toLocaleString() : 'N/A'}
                            {b.schedule_date && (
                              <span style={{ marginLeft: '8px' }}><Clock size={12} style={{ verticalAlign: '-2px' }} /> {new Date(b.schedule_date).toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span className={`badge ${statusBadge}`}>{b.status || 'Draft'}</span>
                          <button
                            className="btn btn-ghost btn-sm btn-icon"
                            onClick={(e) => { e.stopPropagation(); handleViewBroadcast(b); }}
                            title="View details"
                          >
                            <Eye size={16} />
                          </button>
                          {b.status === 'scheduled' && (
                            <>
                              <button
                                className="btn btn-ghost btn-sm btn-icon"
                                onClick={(e) => { e.stopPropagation(); handleRescheduleBroadcast(b); }}
                                title="Reschedule"
                              >
                                <Clock size={16} />
                              </button>
                              <button 
                                className="btn btn-danger btn-sm" 
                                onClick={(e) => { e.stopPropagation(); handleCancelBroadcast(b); }}
                                title="Cancel"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {b.status !== 'sent' && b.status !== 'scheduled' && (
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={(e) => { e.stopPropagation(); handleDeleteBroadcast(b); }}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">
                  <div className="empty-icon"><Mail size={40} strokeWidth={1.5} /></div>
                  <div className="empty-title">No broadcasts found</div>
                  <div className="empty-text">Try adjusting your search or filters</div>
                </div>
              )}

              {/* Broadcast Pagination */}
              {pagination && pagination.last_page > 1 && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid var(--border-light)',
                  marginTop: '4px'
                }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button 
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        if (pagination.current_page > 1) {
                          dispatch(getBroadcasts({ 
                            per_page: itemsPerPage, 
                            page: pagination.current_page - 1,
                            search: searchTerm,
                            status: broadcastFilter
                          }));
                        }
                      }}
                      disabled={pagination.current_page === 1}
                    >
                      ←
                    </button>
                    {getBroadcastPageNumbers().map(page => (
                      <button 
                        key={page}
                        className={`btn btn-sm ${page === pagination.current_page ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => {
                          dispatch(getBroadcasts({ 
                            per_page: itemsPerPage, 
                            page: page,
                            search: searchTerm,
                            status: broadcastFilter
                          }));
                        }}
                      >
                        {page}
                      </button>
                    ))}
                    <button 
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        if (pagination.current_page < pagination.last_page) {
                          dispatch(getBroadcasts({ 
                            per_page: itemsPerPage, 
                            page: pagination.current_page + 1,
                            search: searchTerm,
                            status: broadcastFilter
                          }));
                        }
                      }}
                      disabled={pagination.current_page === pagination.last_page}
                    >
                      →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div>
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-title" style={{ marginBottom: '12px' }}><Smartphone size={16} style={{ verticalAlign: '-3px' }} /> Message Preview</div>
          <div style={{ background: 'var(--navy)', borderRadius: '16px', padding: '12px', marginBottom: '8px' }}>
            <div style={{ background: '#2d3748', borderRadius: '10px', padding: '10px 14px', marginBottom: '6px' }}>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.4)', marginBottom: '3px' }}>{senderId}</div>
              <div 
                className="message-preview" 
                style={{ padding: 0, background: 'none', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,.85)', whiteSpace: 'pre-wrap' }}
              >
                {previewMessage}
              </div>
              {sampleMember && (
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,.2)', marginTop: '4px', borderTop: '1px solid rgba(255,255,255,.05)', paddingTop: '4px' }}>
                  Preview based on: {sampleMember.first_name} {sampleMember.last_name}
                </div>
              )}
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.3)', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '3px' }}>12:00 PM <CheckCheck size={12} /></div>
          </div>
          {sampleMember && (
            <div style={{ 
              fontSize: '10px', 
              color: 'var(--text-light)', 
              padding: '6px 12px',
              background: 'var(--cream)',
              borderRadius: '6px',
              marginTop: '4px'
            }}>
              <User size={12} style={{ verticalAlign: '-2px' }} /> Sample member: {sampleMember.first_name} {sampleMember.last_name}
              {sampleMember.group && ` · ${sampleMember.group}`}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom: '14px' }}><BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Send Summary</div>
          {[
            ['Recipients', selectedLabel],
            ['Est. SMS Credits', smsCount],
            ['Delivery Time', '~3 minutes']
          ].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)' }}>{l}</span>
              <span style={{ fontWeight: '600', color: 'var(--navy)' }}>{v}</span>
            </div>
          ))}
          {scheduleDate && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)' }}>Scheduled For</span>
              <span style={{ fontWeight: '600', color: 'var(--navy)' }}>{new Date(scheduleDate).toLocaleString()}</span>
            </div>
          )}
          <div style={{ marginTop: '14px', padding: '12px', background: 'var(--amber-bg)', borderRadius: '8px', fontSize: '12px', color: 'var(--amber)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> You have <strong>5,234 credits</strong> remaining. This broadcast will use {smsCount} credits.
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .grid-65-35 {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .tabs {
            flex-wrap: wrap;
          }
          .tab {
            flex: none;
            width: 100%;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .message-preview {
          white-space: pre-wrap;
          word-wrap: break-word;
        }
      `}</style>
    </div>
  );
};

export default Broadcast;